import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Inbox, MessagesSquare, Send } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  CARE_COMMUNICATION_DIRECTIONS,
  CARE_COMMUNICATION_STATUSES,
  useCareCommunicationsList,
  type CareCommunicationDirection,
  type CareCommunicationListFilters,
  type CareCommunicationListItem,
  type CareCommunicationStatus,
} from '@/hooks/useCareCommunications';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CodeChip, FilterChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { CareCommunicationPriorityChip } from '@/components/medcom/CareCommunicationPriorityChip';
import { OrganizationName } from '@/components/medcom/OrganizationName';
import { topicStandIn } from '@/lib/medcom/letterDisplay';
import { careCommunicationThreadPath } from '@/lib/medcom/threadDisplay';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { LetterCategory, LetterTopic } from '@/pages/communicate/LetterColumns';
import { formatDanishDateTime } from '@/lib/danishDateTime';

export function CareCommunications(_props: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<CareCommunicationStatus | 'all'>('all');
  const [directionFilter, setDirectionFilter] = useState<CareCommunicationDirection | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filters = useMemo<CareCommunicationListFilters>(
    () => ({
      status: statusFilter,
      direction: directionFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, directionFilter, searchTerm],
  );

  const { data, isLoading, error, refetch } = useCareCommunicationsList(activeClinicId, filters);
  const items = data?.items ?? [];

  const newButton = (
    <Button type="button" onClick={() => navigate('/medcom/letters/new')} disabled={!activeClinicId}>
      {t('communicate.new_message')}
    </Button>
  );

  const columns: TableColumn<CareCommunicationListItem>[] = [
    {
      id: 'category',
      header: t('communicate.col_category'),
      cell: (item) => <LetterCategory category={item.category} />,
    },
    {
      id: 'topic',
      header: t('communicate.col_topic'),
      cell: (item) => <TopicCell item={item} />,
    },
    {
      id: 'direction',
      header: t('communicate.col_direction'),
      cell: (item) => <DirectionLabel direction={item.direction} />,
    },
    {
      id: 'type',
      header: t('communicate.col_type'),
      cell: (item) => <MessageCode code={item.activityCode} />,
      hideOnMobile: true,
    },
    {
      id: 'sender',
      header: t('communicate.col_sender'),
      cell: (item) => (
        <OrganizationName name={item.senderOrganization} sorId={item.senderSor} fallback="—" />
      ),
      hideOnMobile: true,
    },
    {
      id: 'receiver',
      header: t('communicate.col_receiver'),
      cell: (item) => (
        <OrganizationName name={item.receiverOrganization} sorId={item.receiverSor} fallback="—" />
      ),
      hideOnMobile: true,
    },
    {
      id: 'patient',
      header: t('communicate.col_patient'),
      cell: (item) => item.patientName?.trim() || '—',
    },
    {
      id: 'date',
      header: t('communicate.col_date'),
      cell: (item) => <span className={TYPE_TABULAR}>{formatMessageDate(item)}</span>,
    },
    {
      id: 'status',
      header: t('communicate.col_status'),
      cell: (item) => (
        <span className="inline-flex flex-wrap items-center gap-s2">
          <StatusBadge status={item.status} />
          <CareCommunicationPriorityChip priority={item.priority} variant="compact" />
        </span>
      ),
    },
    {
      id: 'ack',
      header: t('communicate.col_ack'),
      cell: (item) => <AckBadge item={item} />,
      hideOnMobile: true,
    },
  ];

  return (
    <div>
      <div className="flex w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center min-h-10">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('medcomSection.title')}</h1>
        </div>
        <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('communicate.search_placeholder')}
            aria-label={t('communicate.search_placeholder')}
          />
        </div>
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
          <div
            className="flex flex-wrap items-center gap-s2"
            role="group"
            aria-label={t('communicate.col_direction')}
          >
            {CARE_COMMUNICATION_DIRECTIONS.map((d) => (
              <FilterChip
                key={d}
                pressed={directionFilter === d}
                onClick={() => setDirectionFilter(d)}
              >
                {t(`communicate.direction_${d}`)}
              </FilterChip>
            ))}
          </div>
          <div className="w-[200px] max-w-full shrink-0">
            <Select
              aria-label={t('communicate.status_all')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as CareCommunicationStatus | 'all')}
              className="w-full"
            >
              {CARE_COMMUNICATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`communicate.status_${s}`)}
                </option>
              ))}
            </Select>
          </div>
          {newButton}
        </div>
      </div>

      <div className="mt-s5">
        {error ? (
          <div className="mb-s4">
            <Banner
              tone="error"
              action={
                <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                  {t('app.retry')}
                </Button>
              }
            >
              {t('communicate.load_error')}
            </Banner>
          </div>
        ) : null}

        {isLoading ? (
          <Card>
            <div className="flex flex-col gap-s3" aria-busy>
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          </Card>
        ) : !data || items.length === 0 ? (
          <Card>
            <EmptyState
              icon={<MessagesSquare className="h-7 w-7" />}
              title={searchTerm ? t('communicate.empty_search_title') : t('communicate.empty_title')}
              action={searchTerm ? undefined : newButton}
            />
          </Card>
        ) : (
          <Card className="p-0 nav:p-0">
            <ResponsiveTable
              columns={columns}
              rows={items}
              getRowKey={(item) => item.id}
              getRowHref={(item) => letterHref(item)}
              getRowLabel={(item) => {
                const categoryName = item.category?.trim()
                  ? t(`communicate.category_${item.category}`, { defaultValue: item.category })
                  : null;
                return (
                  topicStandIn(item.topic, categoryName)?.text ||
                  item.patientName?.trim() ||
                  item.communicationIdentifier
                );
              }}
              empty={null}
              mobileCard={(item) => (
                <div className="w-full text-left">
                  <TopicCell item={item} />
                  <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                    <LetterCategory category={item.category} />
                    {' · '}
                    {item.patientName?.trim() || '—'}
                  </div>
                  <div className={cn(TYPE.small, TYPE_TABULAR, 'mt-s1 text-ds-muted')}>
                    {formatMessageDate(item)}
                  </div>
                  <div className="mt-s2 flex flex-wrap items-center gap-s2">
                    <DirectionLabel direction={item.direction} />
                    <StatusBadge status={item.status} />
                    <CareCommunicationPriorityChip priority={item.priority} variant="compact" />
                    <MessageCode code={item.activityCode} />
                  </div>
                </div>
              )}
            />
          </Card>
        )}
      </div>
    </div>
  );
}

function letterHref(item: CareCommunicationListItem): string {
  return careCommunicationThreadPath(item.communicationIdentifier, item.id);
}

function TopicCell({ item }: { item: CareCommunicationListItem }) {
  const href = letterHref(item);
  return (
    <Link
      to={href}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, 'block font-medium text-ds-text')}
    >
      <LetterTopic topic={item.topic} category={item.category} />
    </Link>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

function formatMessageDate(item: CareCommunicationListItem): string {
  const iso =
    item.direction === 'in' ? (item.receivedAt ?? item.createdAt) : (item.sentAt ?? item.createdAt);
  if (!iso) return '—';
  return formatDanishDateTime(iso);
}

function DirectionLabel({ direction }: { direction: CareCommunicationDirection }) {
  const { t } = useTranslation();
  const incoming = direction === 'in';
  const Icon = incoming ? Inbox : Send;
  return (
    <span className={cn(TYPE.small, 'inline-flex items-center gap-s1 text-ds-text')}>
      <Icon className="h-3.5 w-3.5 text-ds-muted" aria-hidden />
      {incoming ? t('communicate.direction_short_in') : t('communicate.direction_short_out')}
    </span>
  );
}

function looksLikeMedcomCode(value: string): boolean {
  return /^(X[A-Z]{3}\d{0,2}|[A-Z]{3,5}\d{2}|GGOP\d)$/i.test(value.trim());
}

function MessageCode({ code }: { code: string | null }) {
  const { t } = useTranslation();
  if (!code) return <span className="text-ds-muted">—</span>;
  if (looksLikeMedcomCode(code)) {
    return <CodeChip>{code}</CodeChip>;
  }
  return <span>{t(`communicate.activity_${code}`, { defaultValue: '—' })}</span>;
}

const STATUS_TONE: Record<CareCommunicationStatus, StatusTone> = {
  received: 'upcoming',
  validated: 'upcoming',
  queued: 'draft',
  sent: 'upcoming',
  delivered: 'completed',
  acknowledged: 'finalized',
  failed: 'red',
  rejected: 'red',
};

export function StatusBadge({ status }: { status: CareCommunicationStatus }) {
  const { t } = useTranslation();
  return <StatusChip tone={STATUS_TONE[status] ?? 'neutral'}>{t(`communicate.status_${status}`)}</StatusChip>;
}

export function AckBadge({ item }: { item: CareCommunicationListItem }) {
  const { t } = useTranslation();
  const ack = item.latestAcknowledgement;
  if (!ack) {
    return <span className="text-ds-muted">—</span>;
  }
  const tone: StatusTone = ack.ackType === 'AA' ? 'finalized' : ack.ackType === 'AE' ? 'draft' : 'red';
  return (
    <span title={ack.errorMessage ?? undefined}>
      <StatusChip tone={tone}>{t(`communicate.ack_${ack.ackType}`, { defaultValue: ack.ackType })}</StatusChip>
    </span>
  );
}
